'use client';

/**
 * Intent Schedules Section
 *
 * @component_role Explorer section type `intent_schedules`.
 *
 * @business_rule Tree of ingested schedules (:Intent nodes). Fetches via
 *   `intent_list` on mount; each card expands on click to fetch
 *   `intent_lines` and reveal its LineItems. Clicking a schedule card
 *   ALSO fires `intent_grid` for that schedule's lines so the canvas
 *   fills with recommend/consider/alternative tiers. Clicking a single
 *   line row sets `selectedLineSid` on the store so the canvas
 *   highlights/scrolls to that lane.
 *
 * @user_outcome Buyer sees every schedule they've ingested as a card.
 *   Click one → grid canvas loads for that schedule. Expand → see the
 *   lines with their names and enrichment status. Click a line →
 *   canvas focuses that lane.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain discovery (intent / grid)
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, Loader2, Package } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils/cn';
import { useIntentList, useIntentLines, useIntentGrid } from '@/lib/api/hooks';
import { useIntentStore } from '@/lib/stores/intent-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import type {
  ExplorerSectionConfig,
  IntentLineRow,
  IntentSummary,
  LabelsDict,
} from '@/lib/api/types';

export function IntentSchedulesSection({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config,
  labels: L,
}: {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}) {
  const listStrategy = useCapabilityStore((s) => s.strategyName('intent', 'list_intents'));
  const linesStrategy = useCapabilityStore((s) => s.strategyName('intent', 'list_intent_lines'));
  const gridStrategy = useCapabilityStore((s) => s.strategyName('intent', 'grid'));

  const list = useIntentList(listStrategy ?? 'intent_list');
  const lines = useIntentLines(linesStrategy ?? 'intent_lines');
  const grid = useIntentGrid(gridStrategy ?? 'intent_grid');

  const intentSid = useIntentStore((s) => s.intentSid);
  const selectedLineSid = useIntentStore((s) => s.selectedLineSid);
  const setGridResponse = useIntentStore((s) => s.setGridResponse);
  const selectLine = useIntentStore((s) => s.selectLine);
  const setLoading = useIntentStore((s) => s.setLoading);
  const setError = useIntentStore((s) => s.setError);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);

  // Per-schedule expansion + cached lines so expand/collapse is
  // instant on re-click. Keyed by intent_sid.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [linesBySid, setLinesBySid] = useState<Record<string, IntentLineRow[]>>({});

  const items = list.data?.items ?? [];

  const loadGrid = (item: IntentSummary, lineRows: IntentLineRow[]) => {
    if (!lineRows.length) return;
    setLoading(true);
    grid.mutate(
      {
        intent_sid: item.intent_sid,
        queries: lineRows.map((r) => ({ line_sid: r.line_sid, text: r.name })),
        top_k_per_line: 5,
      },
      {
        onSuccess: (data) => setGridResponse(data),
        onError: (err) => {
          setError(err);
          toast.error(`Grid failed: ${err.message}`);
        },
      },
    );
  };

  const handleCardClick = async (item: IntentSummary) => {
    const isOpen = !!expanded[item.intent_sid];
    setExpanded((e) => ({ ...e, [item.intent_sid]: !isOpen }));

    // Propagate the Intent into the copilot details card — carry the
    // full summary on `properties` so IntentDetails can render the
    // brief, coherence, status, etc. without a second fetch.
    selectNode({
      id: item.intent_sid,
      label: item.name || item.intent_sid,
      type: 'intent',
      properties: item as unknown as Record<string, unknown>,
    });

    // Fetch lines if we haven't yet; otherwise reuse cache.
    let lineRows = linesBySid[item.intent_sid];
    if (!lineRows) {
      try {
        const res = await lines.mutateAsync({ intent_sid: item.intent_sid });
        lineRows = res.lines;
        setLinesBySid((m) => ({ ...m, [item.intent_sid]: lineRows }));
      } catch (err) {
        toast.error(`Lines failed: ${(err as Error).message}`);
        return;
      }
    }

    // Selecting a schedule always fills the canvas with its grid.
    loadGrid(item, lineRows);
  };

  const handleLineClick = (lineSid: string) => {
    selectLine(selectedLineSid === lineSid ? null : lineSid);
  };

  if (list.isLoading) {
    return (
      <SectionShell label={L.schedules_section_header ?? 'Schedules'}>
        <div className="px-3 py-2 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" />
          {L.schedules_loading ?? 'Loading schedules…'}
        </div>
      </SectionShell>
    );
  }

  if (list.isError) {
    return (
      <SectionShell label={L.schedules_section_header ?? 'Schedules'}>
        <div className="px-3 py-2 text-xs text-destructive">{list.error.message}</div>
      </SectionShell>
    );
  }

  if (items.length === 0) return null;

  return (
    <SectionShell label={L.schedules_section_header ?? 'Schedules'} count={items.length}>
      <div className="py-1">
        {items.map((item) => (
          <ScheduleCard
            key={item.intent_sid}
            item={item}
            isActive={intentSid === item.intent_sid}
            isOpen={!!expanded[item.intent_sid]}
            onClick={() => handleCardClick(item)}
            lineRows={linesBySid[item.intent_sid]}
            selectedLineSid={selectedLineSid}
            onLineClick={handleLineClick}
          />
        ))}
      </div>
    </SectionShell>
  );
}

// ============================================================
// Section shell (header + body)
// ============================================================

function SectionShell({
  label,
  count,
  children,
}: {
  label: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t border-border">
      <div className="px-3 py-2 flex items-baseline gap-2">
        <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          {label}
        </h3>
        {count != null && (
          <span className="text-[10px] text-muted-foreground">{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

// ============================================================
// Schedule card (collapsed / expanded)
// ============================================================

function ScheduleCard({
  item,
  isActive,
  isOpen,
  onClick,
  lineRows,
  selectedLineSid,
  onLineClick,
}: {
  item: IntentSummary;
  isActive: boolean;
  isOpen: boolean;
  onClick: () => void;
  lineRows: IntentLineRow[] | undefined;
  selectedLineSid: string | null;
  onLineClick: (line_sid: string) => void;
}) {
  return (
    <div className={cn('border-b border-border/60 last:border-b-0')}>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-left',
          'hover:bg-accent/50 transition-colors cursor-pointer',
          isActive && 'bg-accent',
        )}
      >
        {isOpen ? (
          <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
        )}
        <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate">{item.name || item.intent_sid}</p>
          <p className="text-[10px] text-muted-foreground truncate">
            {`${item.line_count} lines`}
            {item.aggregate_enrichment_status
              ? ` · ${item.aggregate_enrichment_status}`
              : ''}
            {item.coherence != null
              ? ` · coh ${(item.coherence * 100).toFixed(0)}%`
              : ''}
          </p>
        </div>
      </button>

      {isOpen && (
        <ul className="pb-1">
          {lineRows === undefined ? (
            <li className="px-8 py-1.5 flex items-center gap-2 text-[11px] text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading lines…
            </li>
          ) : lineRows.length === 0 ? (
            <li className="px-8 py-1.5 text-[11px] text-muted-foreground">
              No lines.
            </li>
          ) : (
            lineRows.map((ln) => (
              <li key={ln.line_sid}>
                <button
                  type="button"
                  onClick={() => onLineClick(ln.line_sid)}
                  className={cn(
                    'w-full flex items-center gap-2 pl-8 pr-3 py-1 text-left',
                    'text-[11px]',
                    'hover:bg-accent/30 transition-colors cursor-pointer',
                    selectedLineSid === ln.line_sid && 'bg-primary/10 text-primary',
                  )}
                  title={ln.name}
                >
                  <Package className="w-3 h-3 text-muted-foreground shrink-0" />
                  <span className="flex-1 min-w-0 truncate">{ln.name || ln.line_sid}</span>
                  {ln.target_price != null && (
                    <span className="text-[10px] text-muted-foreground">
                      ${Number(ln.target_price).toFixed(0)}
                    </span>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
